import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { getDefaultTaxRate, updateDefaultTaxRate, reset } from '../store/slices/taxSlice';

/**
 * Custom hook for managing tax rates
 * @returns {Object} Tax rates state and handlers
 */
const useTaxRates = () => {
  const { t } = useTranslation('hooks');
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { taxRate, isLoading, isSuccess, isError, message } = useSelector((state) => state.tax);

  // Local state for form management
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rate: '',
    isActive: true,
  });
  const [errors, setErrors] = useState({});

  // Fetch tax rate
  const fetchTaxRate = useCallback(async () => {
    dispatch(getDefaultTaxRate());
  }, [dispatch]);

  // Initial data fetch
  useEffect(() => {
    fetchTaxRate();
    return () => {
      dispatch(reset());
    };
  }, [fetchTaxRate, dispatch]);

  // Update form data when tax rate changes
  useEffect(() => {
    if (taxRate) {
      setFormData({
        name: taxRate.name || '',
        description: taxRate.description || '',
        rate: taxRate.rate ? taxRate.rate.toString() : '',
        isActive: taxRate.isActive !== undefined ? taxRate.isActive : true,
      });
    }
  }, [taxRate]);

  // Handle API success and error states
  useEffect(() => {
    if (isError) {
      enqueueSnackbar(message || t('taxRates.errors.generic'), { variant: 'error' });
      dispatch(reset());
    }

    if (isSuccess) {
      dispatch(reset());
    }
  }, [isError, isSuccess, message, dispatch, enqueueSnackbar, t]);

  // Handle form input changes
  const handleInputChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));

      // Clear validation error when field is edited
      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: null,
        }));
      }
    },
    [errors],
  );

  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('taxRates.validation.nameRequired');
    }

    if (!formData.rate || isNaN(Number(formData.rate))) {
      newErrors.rate = t('taxRates.validation.rateInvalid');
    } else {
      const rateValue = Number(formData.rate);
      if (rateValue < 0 || rateValue > 1) {
        newErrors.rate = t('taxRates.validation.rateRange');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) return;

    setSaving(true);

    try {
      const data = {
        ...formData,
        rate: parseFloat(formData.rate),
      };

      await dispatch(updateDefaultTaxRate(data)).unwrap();
      enqueueSnackbar(t('taxRates.success.updated'), { variant: 'success' });
      fetchTaxRate();
    } catch (error) {
      console.error('Error saving tax rate:', error);
      enqueueSnackbar(t('taxRates.errors.saveFailed'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [validateForm, formData, dispatch, enqueueSnackbar, t, fetchTaxRate]);

  return {
    // State
    taxRate,
    isLoading,
    saving,
    formData,
    errors,

    // Handlers
    handleInputChange,
    handleSave,
    fetchTaxRate,
  };
};

export default useTaxRates;
